import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack, Button, Paper } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import studentsData from '../data/students';
import { useToast } from '../context/ToastContext';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EventIcon from '@mui/icons-material/Event';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const MotionPaper = motion(Paper);

const StudentFinancePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [student, setStudent] = useState(null);

  useEffect(() => {
    const foundStudent = studentsData.find((s) => s.id === parseInt(id));
    if (foundStudent) {
      setStudent(foundStudent);
    } else {
      showToast('الطالب غير موجود', 'error');
      navigate('/students');
    }
  }, [id, navigate, showToast]);

  if (!student) return null;

  const summaryCards = [
    {
      label: 'الرصيد الحالي',
      value: `${student.finance?.balance ?? '-'} شيكل`,
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 26 }} />,
      iconColor: '#4338CA',
      iconBg: '#EEF2FF',
    },
    {
      label: 'آخر دفعة',
      value: student.finance?.lastPaymentDate || '-',
      icon: <EventIcon sx={{ fontSize: 26 }} />,
      iconColor: '#0284C7',
      iconBg: '#E0F2FE',
    },
    {
      label: 'المجموع الكلي المدفوع',
      value: `${student.finance?.totalPaid ?? '-'} شيكل`,
      icon: <TrendingUpIcon sx={{ fontSize: 26 }} />,
      iconColor: '#16A34A',
      iconBg: '#DCFCE7',
    },
  ];

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AccountBalanceWalletIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                المعلومات المالية للطالب
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                {student.firstName} {student.lastName}
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/students')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.6)',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': {
                borderColor: '#fff',
                bgcolor: 'rgba(255,255,255,0.12)',
              },
            }}
          >
            الرجوع لجدول الطلاب
          </Button>
        </MotionPaper>

        {/* Financial Summary Cards */}
        <Stack useFlexGap
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          {summaryCards.map((card, index) => (
            <MotionPaper
              key={card.label}
              component={motion.div}
              elevation={0}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.1 + index * 0.08, ease: 'easeOut' }}
              whileHover={{ y: -4 }}
              sx={{
                flex: 1,
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                bgcolor: '#fff',
                boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                border: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  bgcolor: card.iconBg,
                  color: card.iconColor,
                  borderRadius: 2.5,
                  width: 52,
                  height: 52,
                  minWidth: 52,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {card.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 800,
                    color: '#1E1B4B',
                    lineHeight: 1.2,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    wordBreak: 'break-word',
                  }}
                >
                  {card.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6B7280',
                    fontWeight: 500,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                  }}
                >
                  {card.label}
                </Typography>
              </Box>
            </MotionPaper>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default StudentFinancePage;
